import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronsUpDown } from 'lucide-react';
import {
  Sidebar as SidebarRoot,
  SidebarProvider,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarRail,
  SidebarInset,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LucideIcon } from './LucideIcon';
import { Context } from '../../app/Context';
import type { Sidebar as Interface } from '../../design/components';
import { AdaIcon } from '../shared/AdaIcon';

// Export SidebarProvider and SidebarInset for use in Layout
export { SidebarProvider, SidebarInset };

// Inner sidebar content (used inside SidebarProvider)
const SidebarInner: React.FC<Interface> = ({ onAssistantToggle, assistantOpen }) => {
  const [context, setContext] = React.useContext(Context)!;
  const location = useLocation();
  const allModels = [...new Map(context.authn
    .filter(auth => auth.project?.id === context.project?.id)
    .map(auth => auth.capability.model)
    .map(model => [model.id, model])).values()];
  const models = allModels.filter(model => !model.hidden && model.type !== 'personal')
    .sort((a, b) => a.path === '/' ? -1 : b.path === '/' ? 1 : 0);
  // Profile (personal → Users) links from the user avatar instead of the header
  const profilePath = allModels.find(m => m.type === 'personal' && m.reference?.path === '/users')?.path || '';

  // Derive unique projects from all authorizations
  const authorizedProjects = React.useMemo(() => {
    const projectMap = new Map();
    context.authn.forEach(a => {
      if (a.project?.id) projectMap.set(a.project.id, a.project);
    });
    return Array.from(projectMap.values());
  }, [context.authn]);

  // Get user initials for avatar fallback
  const initials = React.useMemo(() => {
    const name = context.user?.name || '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [context.user?.name]);

  return (
    <SidebarRoot collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                    <LucideIcon name="Folder" size="small" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">
                      {context.project?.name ?? 'Select project'}
                    </span>
                    {context.project?.tier && (
                      <span className="truncate text-xs text-muted-foreground">
                        {context.project.tier}
                      </span>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                align="start"
                side="bottom"
                sideOffset={4}
              >
                {authorizedProjects.map((proj) => (
                  <DropdownMenuItem
                    key={proj.id}
                    onClick={() => setContext(prev => ({ ...prev, project: proj }))}
                    className="gap-2 p-2"
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                      <LucideIcon name="Folder" size="small" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium">{proj.name}</span>
                      {proj.tier && (
                        <span className="text-xs text-muted-foreground">{proj.tier}</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {models.map((model) => {
              const isActive = location.pathname === model.path || location.pathname.startsWith(`${model.path}/`);
              return (
                <SidebarMenuItem key={model.id}>
                  <SidebarMenuButton asChild isActive={isActive} tooltip={model.name}>
                    <Link to={model.path}>
                      {model.icon && <LucideIcon name={model.icon} size="small" />}
                      <span>{model.name}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {context.user && (
            <SidebarMenuItem>
              <SidebarMenuButton tooltip={context.user.name} asChild>
                <Link to={profilePath}>
                  <Avatar className="size-6">
                    <AvatarImage src={context.user.pic} alt={context.user.name} />
                    <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="truncate">{context.user.name}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {context.user && (
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Logout" onClick={() => context.iam.logout()}>
                <LucideIcon name="LogOut" size="small" />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={onAssistantToggle}
              isActive={assistantOpen}
              tooltip="Ada"
            >
              <AdaIcon size={20} animated={!assistantOpen} />
              <span>Ada</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </SidebarRoot>
  );
};

// Main export - just the sidebar content (SidebarProvider is in Layout)
export const Sidebar: React.FC<Interface> = ({
  onAssistantToggle,
  assistantOpen,
}) => {
  return <SidebarInner onAssistantToggle={onAssistantToggle} assistantOpen={assistantOpen} />;
};
